import {
  Box,
  Column,
  Columns,
  Stack,
  Text,
  TextLinkButton,
  TooltipRenderer,
} from 'braid-design-system';
import type { Chart } from 'chart.js';
import { parseToRgb, rgbToColorString } from 'polished';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
} from 'react';
import { Line } from 'react-chartjs-2';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

import { DataCircle } from './DataCircle.tsx';
import {
  parentClass,
  filterButtonsClass,
  statsClass,
} from './LineChartWithInteractiveLegend.css';
import { mapSeriesLabelToFilterLabel, type GroupBy } from './config.ts';
import { customPlugins } from './customPlugins.tsx';

import { DividerColumn } from '#src/components/DividerColumn/DividerColumn.tsx';

const setOpacity = (color: string, opacity: number) =>
  rgbToColorString({ ...parseToRgb(color), alpha: opacity });

const DatasetRow = ({
  dataset,
  groupBy,
  onExcludeSeries,
  onFocusSeries,
  onMouseOver,
  onMouseOut,
}: {
  dataset: {
    label: string;
    metrics: {
      total: number;
      percent: number;
    };
    borderColor: string;
    backgroundColor: string;
  };
  groupBy: GroupBy;
  onExcludeSeries?: (label: string) => void;
  onFocusSeries?: (label: string) => void;
  onMouseOver: (label: string) => void;
  onMouseOut: (label: string) => void;
}) => {
  const [isTruncated, setIsTruncated] = useState(false);

  const label = mapSeriesLabelToFilterLabel(dataset.label, groupBy);
  const showButtons = Boolean(onExcludeSeries || onFocusSeries);

  return (
    <Box
      key={dataset.label}
      onMouseOver={() => onMouseOver(dataset.label)}
      onMouseOut={() => onMouseOut(dataset.label)}
      className={showButtons ? parentClass : undefined}
      paddingTop={['none', 'xxsmall']}
      paddingBottom={['none', 'xsmall']}
    >
      <Columns space="small" alignY="top">
        <Column width="content">
          <DataCircle color={dataset.borderColor} />
        </Column>

        <Column>
          <TooltipRenderer
            id={`tooltip-label-${dataset.label}`}
            tooltip={
              <Text>
                <span style={{ overflowWrap: 'anywhere' }}>{label}</span>
              </Text>
            }
          >
            {({ triggerProps }) => (
              <Box {...(isTruncated ? triggerProps : {})}>
                <Stack space="xsmall">
                  <TextWithOnTruncated
                    label={label}
                    onTruncated={setIsTruncated}
                  />

                  <Box position="relative">
                    <Box
                      className={statsClass}
                      position="absolute"
                      top={0}
                      left={0}
                    >
                      <Text tone="secondary" size="small">
                        {dataset.metrics.total.toLocaleString()} (
                        {dataset.metrics.percent.toFixed(2)}%)
                      </Text>
                    </Box>

                    {showButtons && (
                      <Box
                        display="flex"
                        flexDirection="row"
                        gap="xsmall"
                        position="absolute"
                        top={0}
                        left={0}
                        className={filterButtonsClass}
                      >
                        {onFocusSeries ? (
                          <Text tone="secondary" size="small">
                            <TextLinkButton
                              id={`only-show-${label}`}
                              onClick={() =>
                                onFocusSeries(
                                  mapSeriesLabelToFilterLabel(
                                    dataset.label,
                                    groupBy,
                                  ),
                                )
                              }
                            >
                              Filter
                            </TextLinkButton>
                          </Text>
                        ) : null}

                        {onExcludeSeries ? (
                          <Text tone="secondary" size="small">
                            <TextLinkButton
                              id={`exclude-${label}`}
                              onClick={() =>
                                onExcludeSeries(
                                  mapSeriesLabelToFilterLabel(
                                    dataset.label,
                                    groupBy,
                                  ),
                                )
                              }
                            >
                              Exclude
                            </TextLinkButton>
                          </Text>
                        ) : null}
                      </Box>
                    )}
                  </Box>
                </Stack>
              </Box>
            )}
          </TooltipRenderer>
        </Column>
      </Columns>
    </Box>
  );
};

export const LineChartWithInteractiveLegend = ({
  options,
  data,
  groupBy,
  onExcludeSeries,
  onFocusSeries,
  parentRef,
}: {
  options: ComponentProps<typeof Line>['options'];
  groupBy: GroupBy;
  setGroupBy: (groupBy: GroupBy) => void;
  data: {
    labels: string[];
    metrics?: {
      total: number;
      otherPercent: number;
      otherTotal: number;
    };
    datasets: Array<{
      label: string;
      metrics: {
        total: number;
        percent: number;
      };
      data: Array<{ x: string; y: number | null }>;
      borderColor: string;
      backgroundColor: string;
    }>;
  };
  onExcludeSeries?: (label: string) => void;
  onFocusSeries?: (label: string) => void;
  parentRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const ref = useRef<
    Chart<'line', Array<{ x: string; y: number }>> | undefined
  >(undefined);

  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
  const onMouseOver = useCallback(
    (label: string) => setHoveredLabel(label),
    [setHoveredLabel],
  );
  const onMouseOut = useCallback(
    (label: string) =>
      setHoveredLabel((current) => (current === label ? null : current)),
    [setHoveredLabel],
  );

  useIsomorphicLayoutEffect(() => {
    if (ref.current) {
      ref.current.data.datasets.forEach((dataset) => {
        const active = hoveredLabel === null || dataset.label === hoveredLabel;
        dataset.backgroundColor = setOpacity(
          dataset.backgroundColor as string,
          active ? 1 : 0.1,
        );
        dataset.borderColor = setOpacity(
          dataset.borderColor as string,
          active ? 1 : 0.1,
        );
      });

      ref.current.update();
    }
  }, [hoveredLabel]);

  return (
    <Stack space="xsmall">
      <Box paddingY="small">
        <Stack space="xsmall">
          <Columns
            space={{ mobile: 'medium', tablet: 'small' }}
            collapseBelow="tablet"
          >
            {data.datasets.map((dataset, index) => (
              <React.Fragment key={dataset.label}>
                <Column>
                  <DatasetRow
                    dataset={dataset}
                    groupBy={groupBy}
                    onExcludeSeries={onExcludeSeries}
                    onFocusSeries={onFocusSeries}
                    onMouseOver={onMouseOver}
                    onMouseOut={onMouseOut}
                  />
                </Column>

                {index < data.datasets.length - 1 && (
                  <DividerColumn
                    hideHorizontal
                    horizontalToVerticalBreakpoint="tablet"
                  />
                )}
              </React.Fragment>
            ))}
          </Columns>

          {(data.metrics?.otherTotal ?? 0) > 0 ? (
            <Text tone="secondary" size="small">
              Other: {data.metrics?.otherTotal.toLocaleString()} (
              {data.metrics?.otherPercent.toFixed(2)}%)
            </Text>
          ) : null}
        </Stack>
      </Box>

      <Box
        ref={parentRef}
        style={{
          height: '500px',
          maxHeight: '40vh',
          width: '100%',
        }}
      >
        <Line options={options} data={data} ref={ref} plugins={customPlugins} />
      </Box>
    </Stack>
  );
};

const TextWithOnTruncated = ({
  label,
  onTruncated,
}: {
  label: string;
  onTruncated: (isTruncated: boolean) => void;
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      const element = textRef.current?.parentElement;

      if (element) {
        const isTruncated = element.scrollWidth > element.clientWidth;
        setIsTextTruncated(isTruncated);
      }
    };

    const element = textRef.current;
    if (element) {
      const observer = new ResizeObserver(checkTruncation);
      observer.observe(element);
      return () => observer.disconnect();
    }
  }, [label]);

  // Notify when changed
  useEffect(() => {
    onTruncated(isTextTruncated);
  }, [isTextTruncated, onTruncated]);

  return (
    <Text maxLines={1} size="small">
      <span ref={textRef}>{label}</span>
    </Text>
  );
};
